import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TopBar } from "../components/UI";

const API = process.env.REACT_APP_API_URL || "https://g6fw4rjkvb.execute-api.eu-central-1.amazonaws.com/prototype";

const req = async (path, opts = {}) => {
  const token = localStorage.getItem("wine_route_token");
  const r = await fetch(`${API}${path}`, {
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...opts,
  });
  if (!r.ok) {
    const err = new Error(await r.text());
    err.status = r.status;
    throw err;
  }
  return r.json();
};

const ROADS = [
  { slug: "vienna-venice", label: "Vienna – Venice" },
  { slug: "ar-valley", label: "Ahr Valley" },
  { slug: "malopolska", label: "Małopolska" },
];

const S = {
  page: { minHeight: "100vh", background: "#F2F5EC", paddingBottom: 40 },
  container: { maxWidth: 800, margin: "0 auto", padding: "20px 16px" },
  card: { background: "white", borderRadius: 12, padding: 20, marginBottom: 16, border: "1px solid #C4D0A8" },
  label: { fontSize: 10, fontWeight: 600, color: "#6A7E58", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 4 },
  input: { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #C4D0A8", background: "#EDF1E4", color: "#2C3820", fontSize: 13, boxSizing: "border-box" },
  badge: (status) => ({
    fontSize: 10, padding: "2px 8px", borderRadius: 100,
    background: status === "verified" ? "rgba(61,74,26,0.15)" : status === "rejected" ? "rgba(139,26,47,0.15)" : "rgba(136,96,112,0.15)",
    color: status === "verified" ? "#3D4A1A" : status === "rejected" ? "#8B1A2F" : "#886070",
  }),
};

function WineryReviewCard({ winery, onVerified }) {
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const doVerify = async (approved) => {
    if (!approved && !comment.trim()) { alert("Please provide a comment when rejecting."); return; }
    setLoading(true);
    try {
      await req(`/manage/winery/${winery.id}/verify`, {
        method: "POST",
        body: JSON.stringify({ approved, comment }),
      });
      onVerified(winery.id, approved ? "verified" : "rejected");
    } catch (e) { alert("Failed: " + e.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: expanded ? 16 : 0 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1E2C14" }}>{winery.name}</div>
          <div style={{ fontSize: 11, color: "#6A7E58", marginTop: 2 }}>{winery.address || "No address"}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={S.badge(winery.verification_status)}>{winery.verification_status}</span>
          <button onClick={() => setExpanded(e => !e)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#6A7E58", fontSize: 18 }}>
            {expanded ? "▲" : "▼"}
          </button>
        </div>
      </div>

      {expanded && <>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div style={{ gridColumn: "1/-1" }}>
            <label style={S.label}>Description</label>
            <div style={{ fontSize: 13, color: "#2C3820", padding: "8px 10px", background: "#F8FAF4", borderRadius: 8, border: "1px solid #E8EDE0" }}>
              {winery.description || "—"}
            </div>
          </div>
          <div>
            <label style={S.label}>Coordinates</label>
            <div style={{ fontSize: 13, color: "#4A5E38" }}>{winery.lat}, {winery.lng}</div>
          </div>
          <div>
            <label style={S.label}>Price tier</label>
            <div style={{ fontSize: 13, color: "#4A5E38" }}>{"€".repeat(winery.price_tier || 1)}</div>
          </div>
          <div>
            <label style={S.label}>Organic</label>
            <div style={{ fontSize: 13, color: "#4A5E38" }}>{winery.organic ? "Yes ✓" : "No"}</div>
          </div>
          <div>
            <label style={S.label}>Manager email</label>
            <div style={{ fontSize: 13, color: "#4A5E38" }}>{winery.email || "—"}</div>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={S.label}>Verification comment (required for rejection)</label>
          <textarea value={comment} onChange={e => setComment(e.target.value)}
            placeholder="Add notes for the winery manager..."
            style={{ ...S.input, minHeight: 60, resize: "vertical" }} />
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => doVerify(true)} disabled={loading}
            style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: "#3D4A1A", color: "white", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
            ✓ Verify & Approve
          </button>
          <button onClick={() => doVerify(false)} disabled={loading || !comment.trim()}
            style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: "#8B1A2F", color: "white", cursor: "pointer", fontWeight: 600, fontSize: 13, opacity: comment.trim() ? 1 : 0.5 }}>
            ✗ Reject
          </button>
        </div>
      </>}
    </div>
  );
}

export default function RoadRepPage() {
  const navigate = useNavigate();
  const [road, setRoad] = useState("vienna-venice");
  const [wineries, setWineries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(new Map());

  const token = localStorage.getItem("wine_route_token");
  useEffect(() => { if (!token) navigate("/road-rep/login"); }, [token]);
  const signOut = () => { localStorage.removeItem("wine_route_token"); navigate("/road-rep/login"); };

  const load = () => {
    if (!token) return;
    setLoading(true);
    req(`/manage/road/${road}/pending`)
      .then(setWineries)
      .catch(e => { if (e.status === 401) { localStorage.removeItem("wine_route_token"); navigate("/road-rep/login"); } })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [road]);

  const onVerified = (id, status) => {
    setDone(d => new Map(d).set(id, status));
    setWineries(ws => ws.filter(w => w.id !== id));
  };

  const pending = wineries.filter(w => !done.has(w.id));

  return (
    <div style={S.page}>
      <TopBar title="Road Representative" back={() => navigate("/")}
        right={<button onClick={signOut} style={{ fontSize: 12, padding: "6px 12px", borderRadius: 8, border: "1px solid #C4D0A8", background: "transparent", color: "#5A6E48", cursor: "pointer" }}>Sign out</button>} />
      <div style={S.container}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#1E2C14" }}>Winery Verification</div>
            <div style={{ fontSize: 13, color: "#6A7E58", marginTop: 4 }}>
              {pending.length} winer{pending.length !== 1 ? "ies" : "y"} pending review
            </div>
          </div>
          <select value={road} onChange={e => setRoad(e.target.value)}
            style={{ ...S.input, width: "auto" }}>
            {ROADS.map(r => <option key={r.slug} value={r.slug}>{r.label}</option>)}
          </select>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "#6A7E58" }}>Loading…</div>
        ) : pending.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
            <div style={{ fontSize: 15, color: "#3D4A1A", fontWeight: 600 }}>All wineries verified!</div>
            <div style={{ fontSize: 13, color: "#6A7E58", marginTop: 4 }}>No pending reviews for this road.</div>
          </div>
        ) : (
          pending.map(w => <WineryReviewCard key={w.id} winery={w} onVerified={onVerified} />)
        )}
      </div>
    </div>
  );
}
